import { useEffect, useSyncExternalStore } from 'react';
import { axios } from 'setup/axiosSetup';
import type { OfferSection } from './marketGroups';

/**
 * Every sport's offer layout, as the staff arranged it in the admin: the
 * sections a match's markets are shown in (betbigwin's TOP WETTEN, Handicap,
 * Über/Unter ...), their order and the order of the markets in each. Read
 * once and refreshed every few minutes; until it arrives (or for a sport
 * without one) the built-in sections in marketGroups apply.
 */

export type OfferScope = 'prematch' | 'live';

type Layouts = Record<OfferScope, Record<string, OfferSection[]>>;

const REFRESH_MS = 5 * 60 * 1000;

let layouts: Layouts | null = null;
let loadedAt = 0;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function load(): Promise<void> {
  if (loading) return loading;
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  loading = axios
    .get(`${baseUrl}/offer_layout`, { cache: false })
    .then((response) => {
      const data = response.data ?? {};
      layouts = { prematch: data.prematch ?? {}, live: data.live ?? {} };
      listeners.forEach((l) => l());
    })
    // Without the layout the built-in sections still work.
    .catch(() => undefined)
    .finally(() => {
      loadedAt = Date.now();
      loading = null;
    });
  return loading;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The sections of a sport's offer, or null when it has no layout. */
export function useOfferSections(
  sportId: number | null | undefined,
  scope: OfferScope
): OfferSection[] | null {
  const all = useSyncExternalStore(subscribe, () => layouts);
  useEffect(() => {
    if (!layouts || Date.now() - loadedAt > REFRESH_MS) load();
  }, [sportId, scope]);
  if (!sportId) return null;
  return all?.[scope]?.[String(sportId)] ?? null;
}

/** For tests: forget what was loaded. */
export function resetOfferLayouts(next: Layouts | null = null) {
  layouts = next;
  loadedAt = next ? Date.now() : 0;
  listeners.forEach((l) => l());
}
